import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Shield, 
  Key, 
  Download, 
  Database, 
  Copy, 
  Eye, 
  EyeOff, 
  RefreshCw, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  QrCode,
  FileText,
  Settings,
  Zap
} from 'lucide-react';
import { SecurityService } from '@/utils/securityService';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';
import { 
  TwoFactorAuth, 
  ApiKey, 
  DataExportJob, 
  ErpIntegration,
  DataExportRequest,
  ErpIntegrationRequest
} from '@/types/security';

export const AdvancedSecuritySettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();
  const [loading, setLoading] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorAuth | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [exportJobs, setExportJobs] = useState<DataExportJob[]>([]);
  const [erpIntegrations, setErpIntegrations] = useState<ErpIntegration[]>([]);

  // 2FA States
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQRCode, setTwoFAQRCode] = useState('');
  const [twoFABackupCodes, setTwoFABackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // API Key States
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKeyPermissions, setNewApiKeyPermissions] = useState({
    read: true,
    write: false,
    export: false
  });
  const [newApiKey, setNewApiKey] = useState('');

  // Data Export States
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState<'esg_data' | 'carbon_data' | 'risk_data' | 'portfolio_data' | 'supply_chain_data' | 'all_data'>('all_data');
  const [exportFormat, setExportFormat] = useState<'excel' | 'csv' | 'pdf' | 'json'>('excel');

  // ERP Integration States
  const [showErpModal, setShowErpModal] = useState(false);
  const [erpType, setErpType] = useState<'sap' | 'oracle' | 'microsoft_dynamics' | 'salesforce' | 'custom'>('sap');
  const [erpName, setErpName] = useState('');

  useEffect(() => {
    loadSecuritySettings();
  }, []);

  const loadSecuritySettings = async () => {
    try {
      setLoading(true);
      const settings = await SecurityService.getSecuritySettings();
      setTwoFactorStatus(settings.twoFactorAuth);
      // Filter out any null/undefined items and ensure we have valid arrays
      setApiKeys((settings.apiKeys || []).filter(Boolean));
      setExportJobs((settings.dataExportJobs || []).filter(Boolean));
      setErpIntegrations((settings.erpIntegrations || []).filter(Boolean));
    } catch (error: any) {
      console.error('Error loading security settings:', error);
      const errorMessage = error?.message || 'Failed to load security settings';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // 2FA Functions
  const handle2FASetup = async () => {
    try {
      setLoading(true);
      const setup = await SecurityService.twoFactor.setup2FA();
      setTwoFASecret(setup.secret);
      setTwoFAQRCode(setup.qrCodeUrl);
      setTwoFABackupCodes(setup.backupCodes);
      setShow2FASetup(true);
    } catch (error: any) {
      console.error('Error setting up 2FA:', error);
      const errorMessage = error?.message || 'Failed to setup 2FA';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handle2FAEnable = async () => {
    try {
      setLoading(true);
      await SecurityService.twoFactor.enable2FA(twoFASecret, verificationCode);
      setShow2FASetup(false);
      setVerificationCode('');
      await loadSecuritySettings();
      toast({
        title: 'Success',
        description: 'Two-factor authentication has been enabled',
      });
    } catch (error: any) {
      console.error('Error enabling 2FA:', error);
      const errorMessage = error?.message || 'Failed to enable 2FA. Please check your verification code.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handle2FADisable = async () => {
    try {
      setLoading(true);
      await SecurityService.twoFactor.disable2FA();
      await loadSecuritySettings();
      toast({
        title: 'Success',
        description: 'Two-factor authentication has been disabled',
      });
    } catch (error: any) {
      console.error('Error disabling 2FA:', error);
      const errorMessage = error?.message || 'Failed to disable 2FA';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // API Key Functions
  const handleCreateApiKey = async () => {
    try {
      setLoading(true);
      const apiKey = await SecurityService.apiKeys.createApiKey({
        name: newApiKeyName,
        permissions: newApiKeyPermissions
      });
      setNewApiKey(apiKey);
      setNewApiKeyName('');
      setNewApiKeyPermissions({ read: true, write: false, export: false });
      await loadSecuritySettings();
    } catch (error) {
      console.error('Error creating API key:', error);
      toast({
        title: 'Error',
        description: 'Failed to create API key',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApiKey = async (apiKeyId: string) => {
    try {
      setLoading(true);
      await SecurityService.apiKeys.revokeApiKey(apiKeyId);
      await loadSecuritySettings();
      toast({
        title: 'Success',
        description: 'API key has been revoked',
      });
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast({
        title: 'Error',
        description: 'Failed to revoke API key',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'Text copied to clipboard',
    });
  };

  // Data Export Functions
  const handleDataExport = async () => {
    if (!(await guardDownload())) return;

    try {
      setLoading(true);
      const request: DataExportRequest = {
        exportType,
        format: exportFormat
      };
      
      const blob = await SecurityService.dataExport.exportData(request);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sustainability-data-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setShowExportModal(false);
      toast({
        title: 'Success',
        description: 'Data export completed successfully',
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: 'Error',
        description: 'Failed to export data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ERP Integration Functions
  const handleErpIntegration = async () => {
    try {
      setLoading(true);
      const request: ErpIntegrationRequest = {
        integrationType: erpType,
        name: erpName,
        configuration: getDefaultErpConfig(erpType),
        syncFrequency: 'daily'
      };
      
      await SecurityService.erpIntegration.setupIntegration(request);
      setShowErpModal(false);
      setErpName('');
      await loadSecuritySettings();
      toast({
        title: 'Success',
        description: 'ERP integration setup completed',
      });
    } catch (error) {
      console.error('Error setting up ERP integration:', error);
      toast({
        title: 'Error',
        description: 'Failed to setup ERP integration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getDefaultErpConfig = (type: string) => {
    switch (type) {
      case 'sap':
        return {
          systemId: '',
          client: '',
          username: '',
          password: '',
          host: '',
          port: 8000,
          language: 'EN',
          connectionPool: 10
        };
      case 'oracle':
        return {
          host: '',
          port: 1521,
          serviceName: '',
          username: '',
          password: '',
          connectionPool: 10
        };
      default:
        return {};
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'processing':
        return <Badge variant="secondary"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  // Helper function to safely format export type - THIS IS THE FIX
  const formatExportType = (exportType: string | undefined) => {
    if (!exportType) return 'Unknown';
    return (exportType || '').replace(/_/g, ' ');
  };

  // Helper function to safely format integration type
  const formatIntegrationType = (integrationType: string | undefined) => {
    if (!integrationType) return 'Unknown';
    return (integrationType || '').replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading security settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="2fa" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="2fa" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Two-Factor Auth
          </TabsTrigger>
          {/* <TabsTrigger value="api" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            API Access
          </TabsTrigger> */}
          <TabsTrigger value="export" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Data Export
          </TabsTrigger>
          {/* <TabsTrigger value="erp" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            ERP Integration
          </TabsTrigger> */}
        </TabsList>

        {/* Two-Factor Authentication Tab */}
        <TabsContent value="2fa" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account with two-factor authentication
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {twoFactorStatus?.enabled ? (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Two-factor authentication is currently enabled for your account.
                    </AlertDescription>
                  </Alert>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-semibold">Status</h3>
                      <p className="text-sm text-gray-600">
                        Enabled since {twoFactorStatus.setupCompletedAt ? new Date(twoFactorStatus.setupCompletedAt).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                    <Button variant="destructive" onClick={handle2FADisable} disabled={loading}>
                      Disable 2FA
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Two-factor authentication is not enabled. Enable it to secure your account.
                    </AlertDescription>
                  </Alert>
                  <Button onClick={handle2FASetup} disabled={loading}>
                    <Shield className="w-4 h-4 mr-2" />
                    Enable Two-Factor Authentication
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Access Tab */}
        {/* <TabsContent value="api" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Access Management
              </CardTitle>
              <CardDescription>
                Manage API keys for external integrations and data access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">API Keys</h3>
                <Button onClick={() => setShowApiKeyModal(true)}>
                  <Key className="w-4 h-4 mr-2" />
                  Create New API Key
                </Button>
              </div>
              
              {apiKeys.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No API keys created yet</p>
                  <p className="text-sm">Create an API key to enable external integrations</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {apiKeys.map((apiKey, index) => (
                    <div key={apiKey?.id || `api-key-${index}`} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-semibold">{apiKey?.name || 'Unknown API Key'}</h4>
                        <p className="text-sm text-gray-600">
                          Created {apiKey?.createdAt ? new Date(apiKey.createdAt).toLocaleDateString() : 'Unknown date'}
                        </p>
                        <div className="flex gap-2 mt-2">
                          {apiKey?.permissions?.read && <Badge variant="outline">Read</Badge>}
                          {apiKey?.permissions?.write && <Badge variant="outline">Write</Badge>}
                          {apiKey?.permissions?.export && <Badge variant="outline">Export</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={apiKey?.isActive ? "default" : "secondary"}>
                          {apiKey?.isActive ? "Active" : "Inactive"}
                        </Badge>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => apiKey?.id && handleRevokeApiKey(apiKey.id)}
                          disabled={loading || !apiKey?.id}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent> */}

        {/* Data Export Tab */}
        <TabsContent value="export" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Data Export
              </CardTitle>
              <CardDescription>
                Export your sustainability data in various formats
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Export Data</h3>
                <Button onClick={() => setShowExportModal(true)}>
                  <Download className="w-4 h-4 mr-2" />
                  Export Data
                </Button>
              </div>
              
              {exportJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No export jobs yet</p>
                  <p className="text-sm">Export your data to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {exportJobs.slice(0, 5).map((job, index) => (
                    <div key={job?.id || `export-job-${index}`} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-semibold capitalize">
                          {formatExportType(job?.exportType)} Export
                        </h4>
                        <p className="text-sm text-gray-600">
                          {job?.format?.toUpperCase() || 'Unknown'} format • {job?.createdAt ? new Date(job.createdAt).toLocaleDateString() : 'Unknown date'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(job?.status || 'pending')}
                        {job?.status === 'completed' && job?.downloadUrl && (
                          <Button size="sm" variant="outline">
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ERP Integration Tab */}
        {/* <TabsContent value="erp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                ERP Integration
              </CardTitle>
              <CardDescription>
                Connect with external ERP systems for automatic data synchronization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">ERP Connections</h3>
                <Button onClick={() => setShowErpModal(true)}>
                  <Database className="w-4 h-4 mr-2" />
                  Add ERP Connection
                </Button>
              </div>
              
              {erpIntegrations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No ERP integrations configured</p>
                  <p className="text-sm">Connect your ERP system to automatically sync data</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {erpIntegrations.map((integration, index) => (
                    <div key={integration?.id || `erp-${index}`} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-semibold">{integration?.name || 'Unknown Integration'}</h4>
                        <p className="text-sm text-gray-600 capitalize">
                          {formatIntegrationType(integration?.integrationType)} • {integration?.syncFrequency || 'Unknown'} sync
                        </p>
                        {integration?.lastSyncAt && (
                          <p className="text-xs text-gray-500">
                            Last sync: {new Date(integration.lastSyncAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={integration?.isActive ? "default" : "secondary"}>
                          {integration?.isActive ? "Active" : "Inactive"}
                        </Badge>
                        <Button size="sm" variant="outline">
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Test
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent> */}
      </Tabs>

      {/* 2FA Setup Modal */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app and enter the verification code
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center">
              <img src={twoFAQRCode} alt="QR Code" className="w-48 h-48" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <Input
                id="verification-code"
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
              />
            </div>
            <div className="space-y-2">
              <Button variant="outline" onClick={() => setShowBackupCodes(!showBackupCodes)}>
                <FileText className="w-4 h-4 mr-2" />
                {showBackupCodes ? 'Hide' : 'Show'} Backup Codes
              </Button>
              {showBackupCodes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-semibold mb-2">Backup Codes (save these securely):</p>
                  <div className="grid grid-cols-2 gap-2">
                    {twoFABackupCodes.map((code, index) => (
                      <code key={index} className="text-xs bg-white p-1 rounded border">
                        {code}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={handle2FAEnable} disabled={verificationCode.length !== 6 || loading}>
                Enable 2FA
              </Button>
              <Button variant="outline" onClick={() => setShow2FASetup(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* API Key Creation Modal */}
      {/* <Dialog open={showApiKeyModal} onOpenChange={setShowApiKeyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New API Key</DialogTitle>
            <DialogDescription>
              Create an API key for external integrations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-key-name">API Key Name</Label>
              <Input
                id="api-key-name"
                placeholder="e.g., SAP Integration"
                value={newApiKeyName}
                onChange={(e) => setNewApiKeyName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="read-permission"
                    checked={newApiKeyPermissions.read}
                    onCheckedChange={(checked) => setNewApiKeyPermissions(prev => ({ ...prev, read: checked }))}
                  />
                  <Label htmlFor="read-permission">Read Access</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="write-permission"
                    checked={newApiKeyPermissions.write}
                    onCheckedChange={(checked) => setNewApiKeyPermissions(prev => ({ ...prev, write: checked }))}
                  />
                  <Label htmlFor="write-permission">Write Access</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="export-permission"
                    checked={newApiKeyPermissions.export}
                    onCheckedChange={(checked) => setNewApiKeyPermissions(prev => ({ ...prev, export: checked }))}
                  />
                  <Label htmlFor="export-permission">Export Access</Label>
                </div>
              </div>
            </div>
            {newApiKey && (
              <div className="space-y-2">
                <Label>Your API Key (save this securely)</Label>
                <div className="flex gap-2">
                  <Input value={newApiKey} readOnly />
                  <Button variant="outline" onClick={() => copyToClipboard(newApiKey)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  This key will not be shown again. Make sure to save it securely.
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleCreateApiKey} disabled={!newApiKeyName || loading}>
                Create API Key
              </Button>
              <Button variant="outline" onClick={() => setShowApiKeyModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog> */}

      {/* Data Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Data</DialogTitle>
            <DialogDescription>
              Choose what data to export and in what format
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="export-type">Data Type</Label>
              <Select value={exportType} onValueChange={(value: any) => setExportType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_data">All Data</SelectItem>
                  <SelectItem value="esg_data">ESG Data</SelectItem>
                  <SelectItem value="carbon_data">Carbon Data</SelectItem>
                  <SelectItem value="risk_data">Risk Data</SelectItem>
                  <SelectItem value="portfolio_data">Portfolio Data</SelectItem>
                  <SelectItem value="supply_chain_data">Supply Chain Data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="export-format">Format</Label>
              <Select value={exportFormat} onValueChange={(value: any) => setExportFormat(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleDataExport} disabled={loading}>
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </Button>
              <Button variant="outline" onClick={() => setShowExportModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ERP Integration Modal */}
      {/* <Dialog open={showErpModal} onOpenChange={setShowErpModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add ERP Integration</DialogTitle>
            <DialogDescription>
              Connect your ERP system for automatic data synchronization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="erp-name">Integration Name</Label>
              <Input
                id="erp-name"
                placeholder="e.g., Production SAP System"
                value={erpName}
                onChange={(e) => setErpName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="erp-type">ERP System</Label>
              <Select value={erpType} onValueChange={(value: any) => setErpType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sap">SAP</SelectItem>
                  <SelectItem value="oracle">Oracle</SelectItem>
                  <SelectItem value="microsoft_dynamics">Microsoft Dynamics</SelectItem>
                  <SelectItem value="salesforce">Salesforce</SelectItem>
                  <SelectItem value="custom">Custom ERP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleErpIntegration} disabled={!erpName || loading}>
                <Database className="w-4 h-4 mr-2" />
                Setup Integration
              </Button>
              <Button variant="outline" onClick={() => setShowErpModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog> */}
    </div>
  );
};





// import React, { useState, useEffect } from 'react';
// import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Switch } from '@/components/ui/switch';
// import { Badge } from '@/components/ui/badge';
// import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// import { Textarea } from '@/components/ui/textarea';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { Separator } from '@/components/ui/separator';
// import { 
//   Shield, 
//   Key, 
//   Download, 
//   Database, 
//   Copy, 
//   Eye, 
//   EyeOff, 
//   RefreshCw, 
//   CheckCircle, 
//   XCircle,
//   AlertTriangle,
//   QrCode,
//   FileText,
//   Settings,
//   Zap
// } from 'lucide-react';
// import { SecurityService } from '@/utils/securityService';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { useToast } from '@/hooks/use-toast';
// import { 
//   TwoFactorAuth, 
//   ApiKey, 
//   DataExportJob, 
//   ErpIntegration,
//   DataExportRequest,
//   ErpIntegrationRequest
// } from '@/types/security';

// export const AdvancedSecuritySettings = () => {
//   const { user } = useAuth();
//   const { toast } = useToast();
//   const [loading, setLoading] = useState(false);
//   const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorAuth | null>(null);
//   const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
//   const [exportJobs, setExportJobs] = useState<DataExportJob[]>([]);
//   const [erpIntegrations, setErpIntegrations] = useState<ErpIntegration[]>([]);

//   // 2FA States
//   const [show2FASetup, setShow2FASetup] = useState(false);
//   const [twoFASecret, setTwoFASecret] = useState('');
//   const [twoFAQRCode, setTwoFAQRCode] = useState('');
//   const [twoFABackupCodes, setTwoFABackupCodes] = useState<string[]>([]);
//   const [verificationCode, setVerificationCode] = useState('');
//   const [showBackupCodes, setShowBackupCodes] = useState(false);

//   // API Key States
//   const [showApiKeyModal, setShowApiKeyModal] = useState(false);
//   const [newApiKeyName, setNewApiKeyName] = useState('');
//   const [newApiKeyPermissions, setNewApiKeyPermissions] = useState({
//     read: true,
//     write: false,
//     export: false
//   });
//   const [newApiKey, setNewApiKey] = useState('');

//   // Data Export States
//   const [showExportModal, setShowExportModal] = useState(false);
//   const [exportType, setExportType] = useState<'esg_data' | 'carbon_data' | 'risk_data' | 'portfolio_data' | 'supply_chain_data' | 'all_data'>('all_data');
//   const [exportFormat, setExportFormat] = useState<'excel' | 'csv' | 'pdf' | 'json'>('excel');

//   // ERP Integration States
//   const [showErpModal, setShowErpModal] = useState(false);
//   const [erpType, setErpType] = useState<'sap' | 'oracle' | 'microsoft_dynamics' | 'salesforce' | 'custom'>('sap');
//   const [erpName, setErpName] = useState('');

//   useEffect(() => {
//     loadSecuritySettings();
//   }, []);

//   const loadSecuritySettings = async () => {
//     try {
//       setLoading(true);
//       const settings = await SecurityService.getSecuritySettings();
//       setTwoFactorStatus(settings.twoFactorAuth);
//       setApiKeys(settings.apiKeys);
//       setExportJobs(settings.dataExportJobs);
//       setErpIntegrations(settings.erpIntegrations);
//     } catch (error: any) {
//       console.error('Error loading security settings:', error);
//       const errorMessage = error?.message || 'Failed to load security settings';
//       toast({
//         title: 'Error',
//         description: errorMessage,
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 2FA Functions
//   const handle2FASetup = async () => {
//     try {
//       setLoading(true);
//       const setup = await SecurityService.twoFactor.setup2FA();
//       setTwoFASecret(setup.secret);
//       setTwoFAQRCode(setup.qrCodeUrl);
//       setTwoFABackupCodes(setup.backupCodes);
//       setShow2FASetup(true);
//     } catch (error: any) {
//       console.error('Error setting up 2FA:', error);
//       const errorMessage = error?.message || 'Failed to setup 2FA';
//       toast({
//         title: 'Error',
//         description: errorMessage,
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handle2FAEnable = async () => {
//     try {
//       setLoading(true);
//       await SecurityService.twoFactor.enable2FA(twoFASecret, verificationCode);
//       setShow2FASetup(false);
//       setVerificationCode('');
//       await loadSecuritySettings();
//       toast({
//         title: 'Success',
//         description: 'Two-factor authentication has been enabled',
//       });
//     } catch (error: any) {
//       console.error('Error enabling 2FA:', error);
//       const errorMessage = error?.message || 'Failed to enable 2FA. Please check your verification code.';
//       toast({
//         title: 'Error',
//         description: errorMessage,
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handle2FADisable = async () => {
//     try {
//       setLoading(true);
//       await SecurityService.twoFactor.disable2FA();
//       await loadSecuritySettings();
//       toast({
//         title: 'Success',
//         description: 'Two-factor authentication has been disabled',
//       });
//     } catch (error: any) {
//       console.error('Error disabling 2FA:', error);
//       const errorMessage = error?.message || 'Failed to disable 2FA';
//       toast({
//         title: 'Error',
//         description: errorMessage,
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // API Key Functions
//   const handleCreateApiKey = async () => {
//     try {
//       setLoading(true);
//       const apiKey = await SecurityService.apiKeys.createApiKey({
//         name: newApiKeyName,
//         permissions: newApiKeyPermissions
//       });
//       setNewApiKey(apiKey);
//       setNewApiKeyName('');
//       setNewApiKeyPermissions({ read: true, write: false, export: false });
//       await loadSecuritySettings();
//     } catch (error) {
//       console.error('Error creating API key:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to create API key',
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleRevokeApiKey = async (apiKeyId: string) => {
//     try {
//       setLoading(true);
//       await SecurityService.apiKeys.revokeApiKey(apiKeyId);
//       await loadSecuritySettings();
//       toast({
//         title: 'Success',
//         description: 'API key has been revoked',
//       });
//     } catch (error) {
//       console.error('Error revoking API key:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to revoke API key',
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const copyToClipboard = (text: string) => {
//     navigator.clipboard.writeText(text);
//     toast({
//       title: 'Copied',
//       description: 'Text copied to clipboard',
//     });
//   };

//   // Data Export Functions
//   const handleDataExport = async () => {
//     try {
//       setLoading(true);
//       const request: DataExportRequest = {
//         exportType,
//         format: exportFormat
//       };
      
//       const blob = await SecurityService.dataExport.exportData(request);
      
//       // Create download link
//       const url = window.URL.createObjectURL(blob);
//       const a = document.createElement('a');
//       a.href = url;
//       a.download = `sustainability-data-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
//       document.body.appendChild(a);
//       a.click();
//       window.URL.revokeObjectURL(url);
//       document.body.removeChild(a);
      
//       setShowExportModal(false);
//       toast({
//         title: 'Success',
//         description: 'Data export completed successfully',
//       });
//     } catch (error) {
//       console.error('Error exporting data:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to export data',
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ERP Integration Functions
//   const handleErpIntegration = async () => {
//     try {
//       setLoading(true);
//       const request: ErpIntegrationRequest = {
//         integrationType: erpType,
//         name: erpName,
//         configuration: getDefaultErpConfig(erpType),
//         syncFrequency: 'daily'
//       };
      
//       await SecurityService.erpIntegration.setupIntegration(request);
//       setShowErpModal(false);
//       setErpName('');
//       await loadSecuritySettings();
//       toast({
//         title: 'Success',
//         description: 'ERP integration setup completed',
//       });
//     } catch (error) {
//       console.error('Error setting up ERP integration:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to setup ERP integration',
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const getDefaultErpConfig = (type: string) => {
//     switch (type) {
//       case 'sap':
//         return {
//           systemId: '',
//           client: '',
//           username: '',
//           password: '',
//           host: '',
//           port: 8000,
//           language: 'EN',
//           connectionPool: 10
//         };
//       case 'oracle':
//         return {
//           host: '',
//           port: 1521,
//           serviceName: '',
//           username: '',
//           password: '',
//           connectionPool: 10
//         };
//       default:
//         return {};
//     }
//   };

//   const getStatusBadge = (status: string) => {
//     switch (status) {
//       case 'completed':
//         return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
//       case 'processing':
//         return <Badge variant="secondary"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
//       case 'failed':
//         return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
//       default:
//         return <Badge variant="outline">Pending</Badge>;
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center p-8">
//         <RefreshCw className="w-6 h-6 animate-spin" />
//         <span className="ml-2">Loading security settings...</span>
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-6">
//       <Tabs defaultValue="2fa" className="w-full">
//         <TabsList className="grid w-full grid-cols-4">
//           <TabsTrigger value="2fa" className="flex items-center gap-2">
//             <Shield className="h-4 w-4" />
//             Two-Factor Auth
//           </TabsTrigger>
//           <TabsTrigger value="api" className="flex items-center gap-2">
//             <Key className="h-4 w-4" />
//             API Access
//           </TabsTrigger>
//           <TabsTrigger value="export" className="flex items-center gap-2">
//             <Download className="h-4 w-4" />
//             Data Export
//           </TabsTrigger>
//           <TabsTrigger value="erp" className="flex items-center gap-2">
//             <Database className="h-4 w-4" />
//             ERP Integration
//           </TabsTrigger>
//         </TabsList>

//         {/* Two-Factor Authentication Tab */}
//         <TabsContent value="2fa" className="space-y-4">
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Shield className="h-5 w-5" />
//                 Two-Factor Authentication
//               </CardTitle>
//               <CardDescription>
//                 Add an extra layer of security to your account with two-factor authentication
//               </CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               {twoFactorStatus?.enabled ? (
//                 <div className="space-y-4">
//                   <Alert>
//                     <CheckCircle className="h-4 w-4" />
//                     <AlertDescription>
//                       Two-factor authentication is currently enabled for your account.
//                     </AlertDescription>
//                   </Alert>
//                   <div className="flex items-center justify-between p-4 border rounded-lg">
//                     <div>
//                       <h3 className="font-semibold">Status</h3>
//                       <p className="text-sm text-gray-600">
//                         Enabled since {twoFactorStatus.setupCompletedAt ? new Date(twoFactorStatus.setupCompletedAt).toLocaleDateString() : 'Unknown'}
//                       </p>
//                     </div>
//                     <Button variant="destructive" onClick={handle2FADisable} disabled={loading}>
//                       Disable 2FA
//                     </Button>
//                   </div>
//                 </div>
//               ) : (
//                 <div className="space-y-4">
//                   <Alert>
//                     <AlertTriangle className="h-4 w-4" />
//                     <AlertDescription>
//                       Two-factor authentication is not enabled. Enable it to secure your account.
//                     </AlertDescription>
//                   </Alert>
//                   <Button onClick={handle2FASetup} disabled={loading}>
//                     <Shield className="w-4 h-4 mr-2" />
//                     Enable Two-Factor Authentication
//                   </Button>
//                 </div>
//               )}
//             </CardContent>
//           </Card>
//         </TabsContent>

//         {/* API Access Tab */}
//         <TabsContent value="api" className="space-y-4">
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Key className="h-5 w-5" />
//                 API Access Management
//               </CardTitle>
//               <CardDescription>
//                 Manage API keys for external integrations and data access
//               </CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="flex justify-between items-center">
//                 <h3 className="text-lg font-semibold">API Keys</h3>
//                 <Button onClick={() => setShowApiKeyModal(true)}>
//                   <Key className="w-4 h-4 mr-2" />
//                   Create New API Key
//                 </Button>
//               </div>
              
//               {apiKeys.length === 0 ? (
//                 <div className="text-center py-8 text-gray-500">
//                   <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
//                   <p>No API keys created yet</p>
//                   <p className="text-sm">Create an API key to enable external integrations</p>
//                 </div>
//               ) : (
//                 <div className="space-y-3">
//                   {apiKeys.map((apiKey) => (
//                     <div key={apiKey.id} className="flex items-center justify-between p-4 border rounded-lg">
//                       <div className="flex-1">
//                         <h4 className="font-semibold">{apiKey.name}</h4>
//                         <p className="text-sm text-gray-600">
//                           Created {new Date(apiKey.createdAt).toLocaleDateString()}
//                         </p>
//                         <div className="flex gap-2 mt-2">
//                           {apiKey.permissions.read && <Badge variant="outline">Read</Badge>}
//                           {apiKey.permissions.write && <Badge variant="outline">Write</Badge>}
//                           {apiKey.permissions.export && <Badge variant="outline">Export</Badge>}
//                         </div>
//                       </div>
//                       <div className="flex items-center gap-2">
//                         <Badge variant={apiKey.isActive ? "default" : "secondary"}>
//                           {apiKey.isActive ? "Active" : "Inactive"}
//                         </Badge>
//                         <Button
//                           variant="destructive"
//                           size="sm"
//                           onClick={() => handleRevokeApiKey(apiKey.id)}
//                           disabled={loading}
//                         >
//                           Revoke
//                         </Button>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </CardContent>
//           </Card>
//         </TabsContent>

//         {/* Data Export Tab */}
//         <TabsContent value="export" className="space-y-4">
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Download className="h-5 w-5" />
//                 Data Export
//               </CardTitle>
//               <CardDescription>
//                 Export your sustainability data in various formats
//               </CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="flex justify-between items-center">
//                 <h3 className="text-lg font-semibold">Export Data</h3>
//                 <Button onClick={() => setShowExportModal(true)}>
//                   <Download className="w-4 h-4 mr-2" />
//                   Export Data
//                 </Button>
//               </div>
              
//               {exportJobs.length === 0 ? (
//                 <div className="text-center py-8 text-gray-500">
//                   <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
//                   <p>No export jobs yet</p>
//                   <p className="text-sm">Export your data to get started</p>
//                 </div>
//               ) : (
//                 <div className="space-y-3">
//                   {exportJobs.slice(0, 5).map((job) => (
//                     <div key={job.id} className="flex items-center justify-between p-4 border rounded-lg">
//                       <div className="flex-1">
//                         <h4 className="font-semibold capitalize">
//                           {job.exportType.replace('_', ' ')} Export
//                         </h4>
//                         <p className="text-sm text-gray-600">
//                           {job.format.toUpperCase()} format • {new Date(job.createdAt).toLocaleDateString()}
//                         </p>
//                       </div>
//                       <div className="flex items-center gap-2">
//                         {getStatusBadge(job.status)}
//                         {job.status === 'completed' && job.downloadUrl && (
//                           <Button size="sm" variant="outline">
//                             <Download className="w-3 h-3 mr-1" />
//                             Download
//                           </Button>
//                         )}
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </CardContent>
//           </Card>
//         </TabsContent>

//         {/* ERP Integration Tab */}
//         <TabsContent value="erp" className="space-y-4">
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Database className="h-5 w-5" />
//                 ERP Integration
//               </CardTitle>
//               <CardDescription>
//                 Connect with external ERP systems for automatic data synchronization
//               </CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="flex justify-between items-center">
//                 <h3 className="text-lg font-semibold">ERP Connections</h3>
//                 <Button onClick={() => setShowErpModal(true)}>
//                   <Database className="w-4 h-4 mr-2" />
//                   Add ERP Connection
//                 </Button>
//               </div>
              
//               {erpIntegrations.length === 0 ? (
//                 <div className="text-center py-8 text-gray-500">
//                   <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
//                   <p>No ERP integrations configured</p>
//                   <p className="text-sm">Connect your ERP system to automatically sync data</p>
//                 </div>
//               ) : (
//                 <div className="space-y-3">
//                   {erpIntegrations.map((integration) => (
//                     <div key={integration.id} className="flex items-center justify-between p-4 border rounded-lg">
//                       <div className="flex-1">
//                         <h4 className="font-semibold">{integration.name}</h4>
//                         <p className="text-sm text-gray-600 capitalize">
//                           {integration.integrationType.replace('_', ' ')} • {integration.syncFrequency} sync
//                         </p>
//                         {integration.lastSyncAt && (
//                           <p className="text-xs text-gray-500">
//                             Last sync: {new Date(integration.lastSyncAt).toLocaleString()}
//                           </p>
//                         )}
//                       </div>
//                       <div className="flex items-center gap-2">
//                         <Badge variant={integration.isActive ? "default" : "secondary"}>
//                           {integration.isActive ? "Active" : "Inactive"}
//                         </Badge>
//                         <Button size="sm" variant="outline">
//                           <RefreshCw className="w-3 h-3 mr-1" />
//                           Test
//                         </Button>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </CardContent>
//           </Card>
//         </TabsContent>
//       </Tabs>

//       {/* 2FA Setup Modal */}
//       <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
//             <DialogDescription>
//               Scan the QR code with your authenticator app and enter the verification code
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-4">
//             <div className="flex justify-center">
//               <img src={twoFAQRCode} alt="QR Code" className="w-48 h-48" />
//             </div>
//             <div className="space-y-2">
//               <Label htmlFor="verification-code">Verification Code</Label>
//               <Input
//                 id="verification-code"
//                 type="text"
//                 placeholder="Enter 6-digit code"
//                 value={verificationCode}
//                 onChange={(e) => setVerificationCode(e.target.value)}
//                 maxLength={6}
//               />
//             </div>
//             <div className="space-y-2">
//               <Button variant="outline" onClick={() => setShowBackupCodes(!showBackupCodes)}>
//                 <FileText className="w-4 h-4 mr-2" />
//                 {showBackupCodes ? 'Hide' : 'Show'} Backup Codes
//               </Button>
//               {showBackupCodes && (
//                 <div className="p-3 bg-gray-50 rounded-lg">
//                   <p className="text-sm font-semibold mb-2">Backup Codes (save these securely):</p>
//                   <div className="grid grid-cols-2 gap-2">
//                     {twoFABackupCodes.map((code, index) => (
//                       <code key={index} className="text-xs bg-white p-1 rounded border">
//                         {code}
//                       </code>
//                     ))}
//                   </div>
//                 </div>
//               )}
//             </div>
//             <div className="flex gap-2">
//               <Button onClick={handle2FAEnable} disabled={verificationCode.length !== 6 || loading}>
//                 Enable 2FA
//               </Button>
//               <Button variant="outline" onClick={() => setShow2FASetup(false)}>
//                 Cancel
//               </Button>
//             </div>
//           </div>
//         </DialogContent>
//       </Dialog>

//       {/* API Key Creation Modal */}
//       <Dialog open={showApiKeyModal} onOpenChange={setShowApiKeyModal}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle>Create New API Key</DialogTitle>
//             <DialogDescription>
//               Create an API key for external integrations
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-4">
//             <div className="space-y-2">
//               <Label htmlFor="api-key-name">API Key Name</Label>
//               <Input
//                 id="api-key-name"
//                 placeholder="e.g., SAP Integration"
//                 value={newApiKeyName}
//                 onChange={(e) => setNewApiKeyName(e.target.value)}
//               />
//             </div>
//             <div className="space-y-2">
//               <Label>Permissions</Label>
//               <div className="space-y-2">
//                 <div className="flex items-center space-x-2">
//                   <Switch
//                     id="read-permission"
//                     checked={newApiKeyPermissions.read}
//                     onCheckedChange={(checked) => setNewApiKeyPermissions(prev => ({ ...prev, read: checked }))}
//                   />
//                   <Label htmlFor="read-permission">Read Access</Label>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <Switch
//                     id="write-permission"
//                     checked={newApiKeyPermissions.write}
//                     onCheckedChange={(checked) => setNewApiKeyPermissions(prev => ({ ...prev, write: checked }))}
//                   />
//                   <Label htmlFor="write-permission">Write Access</Label>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <Switch
//                     id="export-permission"
//                     checked={newApiKeyPermissions.export}
//                     onCheckedChange={(checked) => setNewApiKeyPermissions(prev => ({ ...prev, export: checked }))}
//                   />
//                   <Label htmlFor="export-permission">Export Access</Label>
//                 </div>
//               </div>
//             </div>
//             {newApiKey && (
//               <div className="space-y-2">
//                 <Label>Your API Key (save this securely)</Label>
//                 <div className="flex gap-2">
//                   <Input value={newApiKey} readOnly />
//                   <Button variant="outline" onClick={() => copyToClipboard(newApiKey)}>
//                     <Copy className="w-4 h-4" />
//                   </Button>
//                 </div>
//                 <p className="text-xs text-gray-500">
//                   This key will not be shown again. Make sure to save it securely.
//                 </p>
//               </div>
//             )}
//             <div className="flex gap-2">
//               <Button onClick={handleCreateApiKey} disabled={!newApiKeyName || loading}>
//                 Create API Key
//               </Button>
//               <Button variant="outline" onClick={() => setShowApiKeyModal(false)}>
//                 Cancel
//               </Button>
//             </div>
//           </div>
//         </DialogContent>
//       </Dialog>

//       {/* Data Export Modal */}
//       <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle>Export Data</DialogTitle>
//             <DialogDescription>
//               Choose what data to export and in what format
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-4">
//             <div className="space-y-2">
//               <Label htmlFor="export-type">Data Type</Label>
//               <Select value={exportType} onValueChange={(value: any) => setExportType(value)}>
//                 <SelectTrigger>
//                   <SelectValue />
//                 </SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="all_data">All Data</SelectItem>
//                   <SelectItem value="esg_data">ESG Data</SelectItem>
//                   <SelectItem value="carbon_data">Carbon Data</SelectItem>
//                   <SelectItem value="risk_data">Risk Data</SelectItem>
//                   <SelectItem value="portfolio_data">Portfolio Data</SelectItem>
//                   <SelectItem value="supply_chain_data">Supply Chain Data</SelectItem>
//                 </SelectContent>
//               </Select>
//             </div>
//             <div className="space-y-2">
//               <Label htmlFor="export-format">Format</Label>
//               <Select value={exportFormat} onValueChange={(value: any) => setExportFormat(value)}>
//                 <SelectTrigger>
//                   <SelectValue />
//                 </SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="excel">Excel (.xlsx)</SelectItem>
//                   <SelectItem value="csv">CSV</SelectItem>
//                   <SelectItem value="pdf">PDF</SelectItem>
//                   <SelectItem value="json">JSON</SelectItem>
//                 </SelectContent>
//               </Select>
//             </div>
//             <div className="flex gap-2">
//               <Button onClick={handleDataExport} disabled={loading}>
//                 <Download className="w-4 h-4 mr-2" />
//                 Export Data
//               </Button>
//               <Button variant="outline" onClick={() => setShowExportModal(false)}>
//                 Cancel
//               </Button>
//             </div>
//           </div>
//         </DialogContent>
//       </Dialog>

//       {/* ERP Integration Modal */}
//       <Dialog open={showErpModal} onOpenChange={setShowErpModal}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle>Add ERP Integration</DialogTitle>
//             <DialogDescription>
//               Connect your ERP system for automatic data synchronization
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-4">
//             <div className="space-y-2">
//               <Label htmlFor="erp-name">Integration Name</Label>
//               <Input
//                 id="erp-name"
//                 placeholder="e.g., Production SAP System"
//                 value={erpName}
//                 onChange={(e) => setErpName(e.target.value)}
//               />
//             </div>
//             <div className="space-y-2">
//               <Label htmlFor="erp-type">ERP System</Label>
//               <Select value={erpType} onValueChange={(value: any) => setErpType(value)}>
//                 <SelectTrigger>
//                   <SelectValue />
//                 </SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="sap">SAP</SelectItem>
//                   <SelectItem value="oracle">Oracle</SelectItem>
//                   <SelectItem value="microsoft_dynamics">Microsoft Dynamics</SelectItem>
//                   <SelectItem value="salesforce">Salesforce</SelectItem>
//                   <SelectItem value="custom">Custom ERP</SelectItem>
//                 </SelectContent>
//               </Select>
//             </div>
//             <div className="flex gap-2">
//               <Button onClick={handleErpIntegration} disabled={!erpName || loading}>
//                 <Database className="w-4 h-4 mr-2" />
//                 Setup Integration
//               </Button>
//               <Button variant="outline" onClick={() => setShowErpModal(false)}>
//                 Cancel
//               </Button>
//             </div>
//           </div>
//         </DialogContent>
//       </Dialog>
//     </div>
//   );
// }; 
